import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from "@mui/material";
import { ButtonCancel, ButtonConfirm } from "components/buttons";
import { MprcButton } from "components/MprcButton";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { MprcSpinnerIndicator } from "components/MprcSpinnerIndicator";
import { useMprcTheme } from "components/MprcThemeProvider";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import * as React from "react";
import { ChangeEvent, useState } from "react";

////////////////////////////////////////////////////////////////////////////////

type InputSelection = {
    locale: string;
    value: Nullable<string>;
    required: boolean;
    isTranslating: boolean;
};

interface IProps {
    locales: LocaleInput[];
    defaultValue?: string;
    currentValues?: LocaleInputCollection,
    maxLength?: number;
    maxRows?: string | number;
    multiline?: boolean;
    onClose: (result?: LocaleInputCollection) => void;
}

////////////////////////////////////////////////////////////////////////////////

function convertLocalesToInput(
    locales: LocaleInput[],
    defaultLocale: string,
    defaultValue?: string,
    currentValues?: LocaleInputCollection,
): InputSelection[] {

    const result: InputSelection[] = [];
    const defaultItem = currentValues?.find(p => p.locale == defaultValue);

    result.push({
        locale: defaultLocale,
        value: defaultItem?.text || defaultValue || null,
        isTranslating: false,
        required: true
    });

    locales.filter(p => p.locale != defaultLocale).forEach((v) => {
        result.push({
            locale: v.locale,
            value: currentValues?.find(x => x.locale == v.locale)?.text || null,
            required: v.required,
            isTranslating: false
        });
    });

    return result;
}

////////////////////////////////////////////////////////////////////////////////

export const MprcMultiLanguageInputDialog: React.FC<IProps> = (props) => {
    const api = useCoreApi();
    const { theme } = useMprcTheme();
    const [state, setState] = useState<InputSelection[]>([]);
    const { currentValues, defaultValue, locales, maxLength, maxRows, multiline, onClose } = props;
    const [defaultLocale] = useState<LocaleInput | undefined>(locales.find(p => p.isDefault));
    const { enqueueSnackbar } = useSnackbar();


    if (defaultLocale == undefined) throw Error("Default locale not found.");

    const getSearchText = () => {
        return state.find(p => p.locale == defaultLocale.locale && p.value)?.value || null;
    };
    ///
    const makeTranslation = () => {
        var toLanguages: string[] = [];

        var searchText = getSearchText();
        if (searchText) {
            state.filter(p => p.locale != defaultValue && (p.value == null || p.value == '')).forEach((item) => {
                toLanguages.push(item.locale);
                item.isTranslating = true;
            });

            if (toLanguages.length > 0) {
                setState([...state]);

                api.v1.azure.translateText({ text: searchText, fromLanguage: defaultLocale.locale, toLanguages })
                    .then((result) => {

                        if (result.data.error) {
                            state
                                .filter(p => p.isTranslating)
                                .forEach((item) => { item.isTranslating = false; });

                            enqueueSnackbar(theme.MprcMultiLanguageInputDialog.errorMessage, { variant: "error" });
                        }
                        else {
                            state
                                .filter(p => p.isTranslating)
                                .forEach((item) => {
                                    item.value = result.data.translations?.find(p => item.locale.startsWith(p.to))?.text || null;
                                    item.isTranslating = false;
                                });
                        }

                        setState([...state]);
                    })
                    .catch((e) => {
                        MprcRequestSnackbarErrorReport(e);
                        state.filter(p => p.isTranslating).forEach((item) => {
                            item.isTranslating = false;
                        });
                        setState([...state]);
                    });
            }
        }
    };

    ///
    const updateRecord = (e: ChangeEvent<HTMLInputElement>) => {
        var item = state.find(p => p.locale == e.currentTarget.name);
        if (item != null) {
            item.value = e.currentTarget.value;
            setState([...state]);
        }
    };

    ///
    const getResult = (): LocaleInputCollection => {
        return state.map((c) => ({ locale: c.locale, text: c.value || null }));
    };

    ///
    const containsError = React.useCallback(() => {
        return state.find(p => p.required && (p.value == undefined || p.value.length == 0)) != null;
    }, [state]);

    ///
    const canTranslate = React.useCallback(() => {
        return getSearchText() != null && state.find(p => p.locale != defaultLocale.locale && (p.value == null || p.value.length == 0)) != null;
    }, [state]);


    ///
    React.useEffect(() => {
        setState(convertLocalesToInput(locales, defaultLocale.locale, defaultValue, currentValues));
    }, []);

    ///
    return (
        <Dialog
            open
            fullWidth
            onClose={() => !containsError() && onClose()}
            maxWidth="xs"
        >
            <DialogTitle>
                <span>{theme.MprcMultiLanguageInputDialog.title}</span>
            </DialogTitle>

            <DialogContent>
                {
                    state.map((v, index) => (
                        <Grid container justifyContent="center" alignItems="center" spacing={2} key={index}>
                            <Grid>
                                <img src={`/img/locales/${v.locale}.png`} width={24} height={24} title={v.locale} />
                            </Grid>
                            <Grid size="grow">
                                <TextField
                                    fullWidth
                                    autoFocus={index == 0}
                                    name={v.locale}
                                    value={v.value ?? ""}
                                    required={v.required}
                                    error={v.required && v.value?.length == 0}
                                    onChange={updateRecord}
                                    disabled={v.isTranslating}
                                    maxRows={maxRows}
                                    multiline={multiline}
                                    slotProps={{
                                        input: {
                                            startAdornment: v.isTranslating && <MprcSpinnerIndicator />,
                                            inputProps: { maxLength }
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    ))}
            </DialogContent>

            <DialogActions>
                <Grid container alignItems="center" spacing={1}>
                    <Grid size="grow">
                        {
                            canTranslate() &&
                            <MprcButton
                                color="warning"
                                variant="contained"
                                onClick={makeTranslation}
                            >
                                <span>{theme.MprcMultiLanguageInputDialog.translateButton}</span>
                            </MprcButton>
                        }
                    </Grid>

                    <Grid>
                        <ButtonCancel
                            variant="contained"
                            onClick={() => onClose()}
                        />
                    </Grid>

                    {
                        <Grid>
                            <ButtonConfirm
                                disabled={containsError()}
                                variant="contained"
                                onClick={() => onClose(getResult())}
                            />
                        </Grid>
                    }
                </Grid>
            </DialogActions>
        </Dialog>
    );
};
